import { useState, useEffect, useId, type FormEvent } from 'react';
import { apiErrorText, type FinCounterpartyRow } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Modal } from '@shared/ui/Modal';
import {
  EMPTY_FORM,
  checkForm,
  formFromCard,
  type CounterpartyForm,
  type CounterpartyFormField,
} from './model';
import {
  FormErrorBox,
  FormGroup,
  FormRow,
  FormRow3,
  Label,
  Input,
  Select,
  BankRequisitesDivider,
  FieldHint,
  ModalBody,
  ModalFooter,
} from './styled';

/** Статьи затрат по умолчанию; статья карточки не из списка показывается как есть. */
const CATEGORIES: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'materials', label: 'Материалы и МТО' },
  { code: 'fuel', label: 'ГСМ (топливо и масла)' },
  { code: 'subcontract', label: 'Субподрядные работы' },
  { code: 'logistics', label: 'Транспорт и доставка' },
  { code: 'leasing', label: 'Лизинг техники' },
  { code: 'payroll', label: 'Оплата труда' },
  { code: 'taxes', label: 'Налоги и сборы' },
  { code: 'services', label: 'Услуги и сервис' },
];

interface CounterpartyModalProps {
  open: boolean;
  onClose: () => void;
  editingItem: FinCounterpartyRow | null;
  /** Сохранить форму; ошибка запроса пробрасывается — окно покажет её и не закроется. */
  onSave: (form: CounterpartyForm) => Promise<void>;
  isSaving: boolean;
}

export function CounterpartyModal({
  open,
  onClose,
  editingItem,
  onSave,
  isSaving,
}: CounterpartyModalProps) {
  const [form, setForm] = useState<CounterpartyForm>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [errorField, setErrorField] = useState<CounterpartyFormField | null>(null);
  const uid = useId();
  const id = (field: CounterpartyFormField) => `${uid}-${field}`;
  const errorId = `${uid}-error`;
  const keyHintId = `${uid}-key-hint`;

  useEffect(() => {
    setForm(editingItem ? formFromCard(editingItem) : EMPTY_FORM);
    setFormError(null);
    setErrorField(null);
  }, [editingItem, open]);

  const set = (field: CounterpartyFormField) => (value: string) =>
    setForm((f) => ({ ...f, [field]: value }));

  /** Связь поля с подписью и с сообщением об ошибке. */
  const fieldProps = (field: CounterpartyFormField) => ({
    id: id(field),
    'aria-invalid': errorField === field ? true : undefined,
    'aria-describedby': errorField === field ? errorId : undefined,
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isSaving) return;
    const problem = checkForm(form, editingItem ? 'edit' : 'create');
    if (problem) {
      setFormError(problem.message);
      setErrorField(problem.field);
      document.getElementById(id(problem.field))?.focus();
      return;
    }
    try {
      setFormError(null);
      setErrorField(null);
      await onSave(form);
      onClose();
    } catch (err: unknown) {
      setFormError(apiErrorText(err, 'Не удалось сохранить контрагента.'));
    }
  };

  const knownCategory = CATEGORIES.some((c) => c.code === form.category);
  const digitsOnly = (v: string) => v.replace(/\D/g, '');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editingItem ? `Редактирование: ${editingItem.name}` : 'Новый контрагент'}
    >
      <ModalBody onSubmit={handleSubmit} noValidate>
        {formError && (
          <FormErrorBox id={errorId} role="alert">
            {formError}
          </FormErrorBox>
        )}

        <FormGroup>
          <Label htmlFor={id('name')}>Рабочее наименование (для реестра)*</Label>
          <Input
            {...fieldProps('name')}
            required
            placeholder="Например, ООО «СеверТрансГрупп»"
            value={form.name}
            onChange={(e) => set('name')(e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor={id('fullName')}>Полное юридическое наименование</Label>
          <Input
            {...fieldProps('fullName')}
            placeholder="Общество с ограниченной ответственностью «СеверТрансГрупп»"
            value={form.fullName}
            onChange={(e) => set('fullName')(e.target.value)}
          />
        </FormGroup>

        <FormRow3>
          <FormGroup>
            <Label htmlFor={id('inn')}>ИНН (10 или 12 цифр)*</Label>
            <Input
              {...fieldProps('inn')}
              required
              inputMode="numeric"
              maxLength={12}
              placeholder="7701234567"
              value={form.inn}
              readOnly={Boolean(editingItem)}
              aria-describedby={editingItem ? keyHintId : fieldProps('inn')['aria-describedby']}
              onChange={(e) => set('inn')(digitsOnly(e.target.value))}
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor={id('kpp')}>КПП (9 цифр)</Label>
            <Input
              {...fieldProps('kpp')}
              inputMode="numeric"
              maxLength={9}
              placeholder={editingItem ? '' : '770101001'}
              value={form.kpp}
              readOnly={Boolean(editingItem)}
              aria-describedby={editingItem ? keyHintId : fieldProps('kpp')['aria-describedby']}
              onChange={(e) => set('kpp')(digitsOnly(e.target.value))}
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor={id('ogrn')}>ОГРН</Label>
            <Input
              {...fieldProps('ogrn')}
              inputMode="numeric"
              maxLength={15}
              placeholder="1157746000000"
              value={form.ogrn}
              onChange={(e) => set('ogrn')(digitsOnly(e.target.value))}
            />
          </FormGroup>
        </FormRow3>
        {editingItem && (
          <FieldHint id={keyHintId}>
            ИНН и КПП у карточки не меняются. Если они другие — это другой контрагент или
            подразделение: заведите для него новую карточку.
          </FieldHint>
        )}

        <FormRow>
          <FormGroup>
            <Label htmlFor={id('type')}>Тип контрагента</Label>
            <Select
              id={id('type')}
              value={form.type}
              onChange={(e) => set('type')(e.target.value)}
            >
              <option value="supplier">Поставщик материалов/оборудования</option>
              <option value="subcontractor">Субподрядчик СМР</option>
              <option value="customer">Заказчик / Генподрядчик</option>
              <option value="leasing">Лизинговая компания</option>
              <option value="bank">Банк / Кредитор</option>
              <option value="tax_authority">ФНС / Налоговый орган</option>
              <option value="other">Прочее</option>
            </Select>
          </FormGroup>
          <FormGroup>
            <Label htmlFor={id('category')}>Статья затрат по умолчанию</Label>
            <Select
              id={id('category')}
              value={form.category}
              onChange={(e) => set('category')(e.target.value)}
            >
              {!knownCategory && <option value={form.category}>{form.category}</option>}
              {CATEGORIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </Select>
          </FormGroup>
        </FormRow>

        <BankRequisitesDivider>
          Банковские реквизиты (для выгрузки в 1С:Клиент-Банк)
        </BankRequisitesDivider>

        <FormRow>
          <FormGroup>
            <Label htmlFor={id('account')}>Расчётный счёт (ровно 20 цифр)</Label>
            <Input
              {...fieldProps('account')}
              inputMode="numeric"
              placeholder="40702810..."
              value={form.account}
              onChange={(e) => set('account')(digitsOnly(e.target.value))}
              maxLength={20}
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor={id('bik')}>БИК банка (ровно 9 цифр)</Label>
            <Input
              {...fieldProps('bik')}
              inputMode="numeric"
              placeholder="044525..."
              value={form.bik}
              onChange={(e) => set('bik')(digitsOnly(e.target.value))}
              maxLength={9}
            />
          </FormGroup>
        </FormRow>

        <FormRow>
          <FormGroup>
            <Label htmlFor={id('bankName')}>Наименование банка</Label>
            <Input
              {...fieldProps('bankName')}
              placeholder="ПАО СБЕРБАНК г. Москва"
              value={form.bankName}
              onChange={(e) => set('bankName')(e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor={id('corrAccount')}>Корр. счёт банка (20 цифр)</Label>
            <Input
              {...fieldProps('corrAccount')}
              inputMode="numeric"
              placeholder="30101810..."
              value={form.corrAccount}
              onChange={(e) => set('corrAccount')(digitsOnly(e.target.value))}
              maxLength={20}
            />
          </FormGroup>
        </FormRow>

        <FormGroup>
          <Label htmlFor={id('notes')}>Примечания</Label>
          <Input
            {...fieldProps('notes')}
            placeholder="Номер рамочного договора, менеджер, контакты…"
            value={form.notes}
            onChange={(e) => set('notes')(e.target.value)}
          />
        </FormGroup>

        {editingItem && (
          <FieldHint>
            Поля, которые вы здесь поменяете, синхронизация с 1С и импорт больше не
            перезаписывают. Остальные обновляются из 1С и файлов.
          </FieldHint>
        )}

        <ModalFooter>
          <Button type="button" $variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" $variant="primary" disabled={isSaving}>
            {isSaving ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </ModalFooter>
      </ModalBody>
    </Modal>
  );
}
